import { Grid } from '@mui/material';
import CardAnalytics from 'views/Production/components/CardAnalytics/CardAnalytics';
import { convertToTwoDecimalPlaces } from 'components/Charts/Tooltip';
import { formatNumberToShortScale } from 'views/Production/Hauler/component/utils';
import { useEffect } from 'react';
import { isNegative, isSameValue, returnAnalysis } from 'utils/fuelAnalytics';
import { useAppSelector } from 'store/hook';
import { convertSeconds } from 'views/Production/AllFleet/utils';
import { useTranslation } from 'react-i18next';

interface Props {
  isLoading: boolean;
  assetOverview: any;
  aggregation: string;
  isSelected: string;
  setAnalyticsData: any;
}

const LoaderAnalyticsWrapper = ({
  isLoading,
  aggregation,
  isSelected,
  setAnalyticsData,
}: Props) => {
  const { t } = useTranslation();
  const {
    data: {
      loaders: { dailyAverages, allSummary },
    },
  } = useAppSelector((state) => state.productionReducer);

  interface ChartEntry {
    name: string;
    value: number;
  }
  type ChartData = Record<string, ChartEntry[]>;
  const chartData: ChartData = {};
  dailyAverages
    ?.filter((item) => item.day !== 'all')
    .forEach((item) => {
      const { day, ...rest } = item;

      Object.entries(rest).forEach(([key, value]) => {
        if (typeof value === 'number') {
          if (!chartData[key]) chartData[key] = [];

          const existing = chartData[key].find((entry) => entry.name === day);

          if (existing) {
            existing.value += value;
          } else {
            chartData[key].push({
              name: day,
              value,
            });
          }
        }
      });
    });

  const allSummaryData = allSummary ?? {};

  const isDayRange =
    ['1D', '7D', '14D', '1M'].includes(isSelected) || aggregation === 'Daily';

  const tr = [
    {
      title: t('production_rate_hour'),
      pointingUp: !isNegative(
        Number(allSummaryData.device_loads_per_hr_percentage_diff ?? 0)
      ),
      value: formatNumberToShortScale(
        convertToTwoDecimalPlaces(
          (allSummaryData.device_loads_per_hr ?? 0).toString()
        )
      ),
      unit: 'Tons/ Hr',
      isGreen: !isNegative(allSummaryData.device_loads_per_hr_percentage_diff),
      analysis: returnAnalysis(
        convertToTwoDecimalPlaces(
          allSummaryData.device_loads_per_hr_percentage_diff?.toString()
        )
      ),
      message: t('from_last_period'),
      hasWarning: false,
      warningCount: 1,
      showProgressBar: true,
      chartData: chartData?.avg_device_loads_per_hr,
      isSame: isSameValue(
        allSummaryData.device_loads_per_hr,
        allSummaryData.device_loads_per_hr_percentage_diff
      ),
      isNotAvailable:
        !allSummaryData.device_loads_per_hr_percentage_diff ||
        allSummaryData.device_loads_per_hr_percentage_diff === undefined,
    },
    {
      title: `Avg Load / ${isDayRange ? 'Day' : 'Month'}`,
      pointingUp: !isNegative(
        Number(allSummaryData.device_loads_per_day_percentage_diff ?? 0)
      ),
      value: formatNumberToShortScale(
        convertToTwoDecimalPlaces(
          (allSummaryData.device_loads_per_day ?? 0).toString()
        )
      ),
      unit: `Tons / ${isDayRange ? 'Day' : 'Month'}`,
      isGreen: !isNegative(allSummaryData.device_loads_per_day_percentage_diff),
      analysis: returnAnalysis(
        convertToTwoDecimalPlaces(
          allSummaryData.device_loads_per_day_percentage_diff?.toString()
        )
      ),
      message: t('from_last_period'),
      hasWarning: false,
      warningCount: 1,
      showProgressBar: false,
      chartData: chartData?.avg_device_loads_per_day,
      isSame: isSameValue(
        allSummaryData.device_loads_per_day,
        allSummaryData.device_loads_per_day_percentage_diff
      ),
      isNotAvailable:
        !allSummaryData.device_loads_per_day_percentage_diff ||
        allSummaryData.device_loads_per_day_percentage_diff === undefined,
    },
    {
      title: t('avg_loader_cycle_time'),
      pointingUp: !isNegative(
        Number(allSummaryData.device_loader_cycle_time_percentage_diff ?? 0)
      ),
      value: formatNumberToShortScale(
        convertToTwoDecimalPlaces(
          convertSeconds(
            allSummaryData.device_loader_cycle_time ?? 0
          ).value.toString()
        )
      ),
      unit:
        (convertSeconds(allSummaryData.device_loader_cycle_time ?? 0).unit ??
          t('minutes_unit')) +
        ' ' +
        t('bucket'),
      isGreen: isNegative(
        allSummaryData.device_loader_cycle_time_percentage_diff
      ),
      analysis: returnAnalysis(
        convertToTwoDecimalPlaces(
          allSummaryData.device_loader_cycle_time_percentage_diff?.toString()
        )
      ),
      message: t('from_last_period'),
      hasWarning: false,
      warningCount: 0,
      showProgressBar: false,
      chartData: chartData?.avg_device_loader_cycle_time,
      isSame: isSameValue(
        allSummaryData.device_loader_cycle_time,
        allSummaryData.device_loader_cycle_time_percentage_diff
      ),
      isNotAvailable:
        !allSummaryData.device_loader_cycle_time_percentage_diff ||
        allSummaryData.device_loader_cycle_time_percentage_diff === undefined,
    },
    {
      title: t('avg_hauler_load_time'),
      pointingUp: !isNegative(
        Number(allSummaryData.device_hauler_load_time_percentage_diff ?? 0)
      ),
      value: formatNumberToShortScale(
        convertToTwoDecimalPlaces(
          convertSeconds(
            allSummaryData.device_hauler_load_time ?? 0
          ).value.toString()
        )
      ),
      unit:
        (convertSeconds(allSummaryData.device_hauler_load_time ?? 0).unit ??
          t('minutes_unit')) +
        ' ' +
        t('day'),
      isGreen: isNegative(
        Number(allSummaryData.device_hauler_load_time_percentage_diff)
      ),
      analysis: returnAnalysis(
        convertToTwoDecimalPlaces(
          allSummaryData.device_hauler_load_time_percentage_diff?.toString()
        )
      ),
      message: t('from_last_period'),
      hasWarning: false,
      warningCount: 0,
      showProgressBar: false,
      // progressBarValue: productionValue,
      chartData: chartData?.avg_device_hauler_load_time,
      isSame: isSameValue(
        allSummaryData.device_hauler_load_time,
        allSummaryData.device_hauler_load_time_percentage_diff
      ),
      isNotAvailable:
        !allSummaryData.device_hauler_load_time_percentage_diff ||
        allSummaryData.device_hauler_load_time_percentage_diff === undefined,
    },
  ];

  useEffect(() => {
    setAnalyticsData((prev: any) =>
      JSON.stringify(prev) !== JSON.stringify(tr) ? tr : prev
    );
  }, [tr, setAnalyticsData]);

  return (
    <Grid
      container
      spacing={2}
      justifyContent="space-between"
      sx={{
        paddingTop: '15px',
      }}
    >
      {tr.map((item, index) => {
        return (
          <Grid item key={index} lg={3} md={6} sm={12} xs={12}>
            <CardAnalytics
              analytics={item}
              isLoading={isLoading}
              isSelected={isSelected}
              aggregation={aggregation}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

export default LoaderAnalyticsWrapper;
